import { useEffect, useRef, useState } from "react";
import { CheckCircle, ArrowsClockwise, XCircle, Play, Pause } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { TahfidzPanel } from "@/components/TahfidzPanel";
import { ReactionThread } from "@/components/ReactionThread";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CATS = [
  ["tahfidz", "Tahfidz"], ["doa", "Doa"], ["hadits", "Hadits"],
  ["arab", "B. Arab"], ["inggris", "B. Inggris"], ["lainnya", "Lainnya"],
];
const STATUSES = [
  ["lancar", "Lancar", "bg-green-600", CheckCircle],
  ["ulang", "Belum Lancar", "bg-yellow-500", ArrowsClockwise],
  ["belum", "Belum Hafal", "bg-red-500", XCircle],
];

export function HafalanRekap({ studentId, canEdit = true, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("tahfidz");
  const [catalog, setCatalog] = useState({ tahfidz: [], doa: [], hadits: [], arab: [], inggris: [], lainnya: [] });
  const [surah, setSurah] = useState([]);
  const [progress, setProgress] = useState([]);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/progress/catalog").then((r) => setCatalog(r.data));
    api.get("/tahfidz/surah").then((r) => setSurah(r.data));
  }, []);
  const load = () => { if (studentId) api.get(`/progress?student_id=${studentId}`).then((r) => setProgress(r.data)); };
  useEffect(() => { load(); }, [studentId]); // eslint-disable-line

  const stop = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setPlaying(null); };
  const play = (it) => { if (playing === it.key) { stop(); return; } stop(); const a = new Audio(it.audio); a.onended = () => setPlaying(null); a.play().catch(() => {}); audioRef.current = a; setPlaying(it.key); };

  const pct = (cat) => {
    const items = progress.filter((p) => p.category === cat);
    if (cat === "tahfidz") {
      const totalAyat = surah.reduce((a, s) => a + (s.ayat || 0), 0);
      const lancarAyat = items.filter((p) => p.status === "lancar" && p.item_key.includes(":")).length;
      return totalAyat ? Math.round((lancarAyat / totalAyat) * 100) : 0;
    }
    const lancar = items.filter((p) => p.status === "lancar").length;
    const total = (catalog[cat] || []).length;
    return total ? Math.round((lancar / total) * 100) : 0;
  };

  const setStatus = async (key, label, status) => {
    try { await api.post("/progress", { student_id: studentId, category: tab, item_key: key, item_label: label, status }); load(); onSaved && onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const byKey = {};
  progress.filter((p) => p.category === tab).forEach((p) => { byKey[p.item_key] = p; });
  const items = tab === "tahfidz" ? [] : (catalog[tab] || []).map((c) => ({ ...c, prog: byKey[c.key] }));

  return (
    <div>
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="haf-summary">
        {CATS.map(([v, l]) => (
          <div key={v} className="bg-white rounded-xl border border-stone-200/60 p-2.5 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold text-stone-600">{l}</span>
              <span className="text-[11px] font-bold text-primary" data-testid={`haf-pct-${v}`}>{pct(v)}%</span>
            </div>
            <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: `${pct(v)}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
        {CATS.map(([v, l]) => (
          <button key={v} data-testid={`sp-haf-tab-${v}`} onClick={() => { stop(); setTab(v); }}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold ${tab === v ? "bg-primary text-white shadow" : "bg-white border border-stone-200 text-stone-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "tahfidz" ? (
        <TahfidzPanel studentId={studentId} mode={canEdit ? "input" : "view"} onSaved={onSaved} />
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.key} data-testid={`sp-haf-item-${tab}-${it.key}`} className="bg-white rounded-2xl border border-stone-200/60 p-3 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <p className="flex-1 font-semibold text-stone-800 text-sm">{it.label || it.arabic || "Item"}</p>
                {it.audio && (
                  <button data-testid={`sp-haf-play-${it.key}`} onClick={() => play(it)}
                    className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center active:scale-90 transition-transform">
                    {playing === it.key ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                  </button>
                )}
              </div>
              {it.arabic && <p className="font-arabic text-2xl text-right text-stone-800 leading-loose" dir="rtl">{it.arabic}</p>}
              {it.latin && <p className="text-primary/80 italic text-xs">{it.latin}</p>}
              {(it.translation || it.meaning) && <p className="text-stone-600 text-xs mb-1">{it.translation || it.meaning}</p>}
              {it.example && <p className="text-stone-500 text-xs italic mb-1">"{it.example}"</p>}
              {canEdit ? (
                <div className="flex gap-2">
                  {STATUSES.map(([val, lbl, bg, Icon]) => {
                    const on = it.prog?.status === val;
                    return (
                      <button key={val} data-testid={`sp-status-${tab}-${it.key}-${val}`} onClick={() => setStatus(it.key, it.label || it.arabic, val)}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${on ? `${bg} text-white` : "bg-stone-100 text-stone-500"}`}>
                        <Icon size={13} weight="fill" /> {lbl}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <span className="text-xs font-semibold text-stone-500">{it.prog?.status || "Belum dinilai"}</span>
              )}
              <ReactionThread studentId={studentId} category={tab} itemKey={it.key} itemLabel={it.label || it.arabic} thread={it.prog?.thread || []} onPosted={load} canEdit={isAdmin || user?.role === "guru"} />
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-stone-400 mt-8">Belum ada materi. Tambahkan di menu Kelola Konten Hafalan.</p>}
        </div>
      )}
    </div>
  );
}
